import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock, AlertTriangle, Clock,
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, X, Info, Check, Loader2, Clock4,
} from "lucide-react";
import {
  activityMeta, formatActivityWhen,
  isActivityOverdue, isActivityDueToday, todayISO,
} from "./leadHelpers";

/* Month-grid calendar view for the admin Calendar tab.
 *
 * - Reads scheduled_activities from the leads list (status === "pending").
 * - Lays them out on a 6×7 month grid (Sunday-first to match US convention).
 * - Each event chip shows activity type + business name; click → /admin/leads/:id.
 * - Clicking an empty day opens a small read-only hint popover guiding the
 *   admin to schedule from a lead detail page (no risky scheduling modal). */

const WEEK_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function ymdString(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function parseYmdToDate(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
}

/* Build the visible month grid: list of { date, ymd, inMonth, isToday } for
 * 6 rows × 7 cols starting from the Sunday before/on the first day of month. */
function buildMonthGrid(year, monthIdx, todayStr) {
  const first = new Date(year, monthIdx, 1, 12, 0, 0);
  const startDow = first.getDay(); // 0 = Sun
  const start = new Date(first);
  start.setDate(first.getDate() - startDow);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const y = d.getFullYear();
    const m = d.getMonth();
    const day = d.getDate();
    const ymd = ymdString(y, m, day);
    cells.push({
      date: d,
      ymd,
      day,
      inMonth: m === monthIdx,
      isToday: ymd === todayStr,
    });
  }
  return cells;
}

/* Map all pending activities by ymd for fast cell lookup. */
function buildActivityMap(leads) {
  const map = new Map();
  for (const l of leads || []) {
    for (const a of (l.scheduled_activities || [])) {
      if (a.status !== "pending") continue;
      if (!a.date) continue;
      const key = a.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ activity: a, lead: l });
    }
  }
  // Sort within each day by time asc.
  for (const arr of map.values()) {
    arr.sort((x, y) => (x.activity.time || "00:00").localeCompare(y.activity.time || "00:00"));
  }
  return map;
}

/* Color tokens by item state — keeps the grid scannable.
 * Overdue (rose) > Today (accent) > Appointment (indigo) > Follow-up (slate). */
/* Chip color tokens — match the new month-view legend exactly:
 * Overdue=#DC2626 / Today=#0072FF / Appointment=#7C3AED / Follow-up=#16A34A. */
function chipClasses(item, todayStr) {
  const overdue = isActivityOverdue(item.activity);
  const due = isActivityDueToday(item.activity) || item.activity.date === todayStr;
  if (overdue) return "bg-[#FEE2E2] text-[#991B1B] border-[#DC2626] hover:border-[#7F1D1D]";
  if (due) return "bg-[#EAF3FF] text-[#0072FF] border-[#0072FF] hover:border-[#061A3A]";
  const meta = activityMeta(item.activity.type);
  if (meta.group === "appointment") return "bg-[#F3E8FF] text-[#7C3AED] border-[#7C3AED] hover:border-[#5B21B6]";
  return "bg-[#DCFCE7] text-[#15803D] border-[#16A34A] hover:border-[#14532D]";
}

/* Small ✓ button used inside event chips + popover items.
 * Uses stopPropagation so the parent Link/button doesn't fire. */
function CompleteButton({ leadId, activityId, onComplete, testId }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onComplete || !activityId || busy) return;
    setBusy(true);
    try { await onComplete(leadId, activityId); } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      data-testid={testId}
      title="Mark complete"
      aria-label="Mark activity complete"
      className="inline-flex items-center justify-center w-4 h-4 rounded text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-300 hover:border-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
    >
      {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" strokeWidth={3} />}
    </button>
  );
}

/* Small snooze button — only renders for overdue items. Pushes the activity
 * to tomorrow (same time) via the same PATCH endpoint Complete uses. */
function SnoozeButton({ leadId, activityId, onSnooze, testId }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onSnooze || !activityId || busy) return;
    setBusy(true);
    try { await onSnooze(leadId, activityId); } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      data-testid={testId}
      title="Snooze to tomorrow"
      aria-label="Snooze activity to tomorrow"
      className="inline-flex items-center justify-center w-4 h-4 rounded text-amber-700 hover:text-white hover:bg-amber-600 border border-amber-300 hover:border-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
    >
      {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Clock4 className="w-2.5 h-2.5" strokeWidth={2.5} />}
    </button>
  );
}

/* Single event chip — renders the activity, its time, business name, a
 * ✓ button to mark complete, AND a snooze button when the chip is overdue.
 * Uses a flex container so the link/text stays separate from the buttons. */
function MonthEventChip({ item, todayStr, onComplete, onSnooze }) {
  const meta = activityMeta(item.activity.type);
  const cls = chipClasses(item, todayStr);
  const overdue = isActivityOverdue(item.activity);
  const labelText = `${meta.label}: ${item.lead.business_name || item.lead.contact_name || "Unnamed"}`;
  const eventKey = item.activity.id || `${item.activity.date}-${item.activity.time || "x"}`;
  return (
    <div
      className={`flex items-center gap-1 rounded border ${cls} px-1.5 py-1 transition-colors`}
      title={`${meta.label} — ${item.lead.business_name || item.lead.contact_name || "Unnamed"} ${item.activity.time ? "· " + item.activity.time : ""}`}
    >
      <Link
        to={`/admin/leads/${item.lead.id}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`month-event-${item.lead.id}-${item.activity.id || item.activity.date}`}
        className="flex-1 min-w-0 truncate text-[10.5px] leading-tight"
      >
        <span className="font-bold mr-1 tabular-nums">{item.activity.time || ""}</span>
        <span className="font-semibold">{labelText}</span>
      </Link>
      {item.activity.id ? (
        <>
          {overdue && onSnooze ? (
            <SnoozeButton
              leadId={item.lead.id}
              activityId={item.activity.id}
              onSnooze={onSnooze}
              testId={`month-event-snooze-${item.lead.id}-${eventKey}`}
            />
          ) : null}
          <CompleteButton
            leadId={item.lead.id}
            activityId={item.activity.id}
            onComplete={onComplete}
            testId={`month-event-complete-${item.lead.id}-${eventKey}`}
          />
        </>
      ) : null}
    </div>
  );
}

/* Day-cell popover — list of activities on this day + read-only schedule hint. */
function DayPopover({ open, anchor, ymd, items, onClose, onComplete, onSnooze }) {
  if (!open || !anchor) return null;
  // Position the popover absolutely below the day cell. We keep it inside the
  // page via right/left clamping using offsetParent dimensions.
  const rect = anchor.getBoundingClientRect();
  const top = rect.bottom + window.scrollY + 6;
  const left = Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - 320 - 8));
  const dateLabel = (() => {
    try {
      return parseYmdToDate(ymd).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch { return ymd; }
  })();

  return (
    <>
      <button
        type="button"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-transparent cursor-default"
        aria-label="Close day details"
      />
      <div
        role="dialog"
        data-testid={`calendar-day-popover-${ymd}`}
        style={{ top, left }}
        className="absolute z-50 w-[320px] bg-white rounded-xl shadow-[0_20px_40px_-12px_rgba(6,26,58,0.35)] border border-slate-200 overflow-hidden"
      >
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[var(--swipe-accent)]">
              {items.length} {items.length === 1 ? "activity" : "activities"}
            </div>
            <div className="font-display text-[14px] font-extrabold text-[var(--swipe-navy)]">{dateLabel}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="calendar-day-popover-close"
            className="w-7 h-7 rounded-lg hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-[260px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-4 text-[13px] text-slate-500 flex items-start gap-2">
              <Info className="w-4 h-4 text-[var(--swipe-accent)] mt-0.5 shrink-0" />
              <span>Tip: schedule reminders from any lead’s detail page.</span>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it) => {
                const meta = activityMeta(it.activity.type);
                const itemKey = `${it.lead.id}-${it.activity.id || `${it.activity.date}-${it.activity.time || "x"}`}`;
                return (
                  <li key={itemKey} className="flex items-stretch">
                    <Link
                      to={`/admin/leads/${it.lead.id}`}
                      onClick={onClose}
                      data-testid={`calendar-day-popover-item-${it.lead.id}`}
                      className="flex items-start gap-2 px-4 py-2.5 hover:bg-[var(--swipe-accent-soft)]/30 transition-colors flex-1 min-w-0"
                    >
                      <div className="text-[11px] uppercase font-bold text-[var(--swipe-accent)] tracking-wider min-w-[42px] mt-0.5">
                        {it.activity.time || "—"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-[13.5px] text-[var(--swipe-navy)] truncate">
                          {it.lead.business_name || it.lead.contact_name || "Unnamed lead"}
                        </div>
                        <div className="text-[11.5px] text-slate-600 truncate">
                          {meta.label} · {meta.group === "appointment" ? "Appointment" : "Follow-up"}
                        </div>
                      </div>
                    </Link>
                    {it.activity.id ? (
                      <div className="flex items-center gap-1.5 pr-3">
                        {isActivityOverdue(it.activity) && onSnooze ? (
                          <SnoozeButton
                            leadId={it.lead.id}
                            activityId={it.activity.id}
                            onSnooze={onSnooze}
                            testId={`calendar-day-popover-snooze-${it.lead.id}-${it.activity.id}`}
                          />
                        ) : null}
                        <CompleteButton
                          leadId={it.lead.id}
                          activityId={it.activity.id}
                          onComplete={onComplete}
                          testId={`calendar-day-popover-complete-${it.lead.id}-${it.activity.id}`}
                        />
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

export default function MonthGridView({ leads, onComplete, onSnooze }) {
  const today = todayISO();
  const [yearMonth, setYearMonth] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [popover, setPopover] = useState(null); // { ymd, anchor, items }

  const activityMap = useMemo(() => buildActivityMap(leads), [leads]);
  const cells = useMemo(
    () => buildMonthGrid(yearMonth.y, yearMonth.m, today),
    [yearMonth, today],
  );

  const monthlyTotal = useMemo(() => {
    let n = 0;
    for (const c of cells) {
      if (!c.inMonth) continue;
      const arr = activityMap.get(c.ymd) || [];
      n += arr.length;
    }
    return n;
  }, [cells, activityMap]);

  const goPrev = () => setYearMonth(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  const goNext = () => setYearMonth(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));
  const goToday = () => {
    const d = new Date();
    setYearMonth({ y: d.getFullYear(), m: d.getMonth() });
  };

  const onDayClick = (cell, e) => {
    const items = activityMap.get(cell.ymd) || [];
    setPopover({ ymd: cell.ymd, anchor: e.currentTarget, items });
  };

  return (
    <div
      data-testid="calendar-month-view"
      className="rounded-2xl border-2 border-[#0072FF] bg-[#EAF3FF]/40 p-4 sm:p-5 space-y-4"
    >
      {/* Header: month nav + today + total */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            data-testid="month-prev"
            aria-label="Previous month"
            className="w-9 h-9 rounded-lg border-2 border-[#0072FF] bg-white text-[#0072FF] hover:bg-[#0072FF] hover:text-white inline-flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={goToday}
            data-testid="month-today"
            className="px-3 h-9 rounded-lg bg-[#0072FF] hover:bg-[#0072FF]/90 text-white text-[12.5px] font-bold inline-flex items-center transition-colors shadow-sm"
          >
            Today
          </button>
          <button
            type="button"
            onClick={goNext}
            data-testid="month-next"
            aria-label="Next month"
            className="w-9 h-9 rounded-lg border-2 border-[#0072FF] bg-white text-[#0072FF] hover:bg-[#0072FF] hover:text-white inline-flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <h3
            data-testid="month-title"
            className="ml-2 font-display text-[20px] font-extrabold text-[var(--swipe-navy)]"
          >
            {MONTH_LABELS[yearMonth.m]} {yearMonth.y}
          </h3>
        </div>
        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap text-[13px] font-bold">
          <span className="text-[#061A3A]">
            <span className="tabular-nums">{monthlyTotal}</span>{" "}
            pending activit{monthlyTotal === 1 ? "y" : "ies"} this month
          </span>
          <span className="inline-flex items-center gap-1.5 text-[#DC2626]">
            <span className="w-3 h-3 rounded-sm bg-[#DC2626]" />
            Overdue
          </span>
          <span className="inline-flex items-center gap-1.5 text-[#0072FF]">
            <span className="w-3 h-3 rounded-sm bg-[#0072FF]" />
            Today
          </span>
          <span className="inline-flex items-center gap-1.5 text-[#7C3AED]">
            <span className="w-3 h-3 rounded-sm bg-[#7C3AED]" />
            Appointment
          </span>
          <span className="inline-flex items-center gap-1.5 text-[#16A34A]">
            <span className="w-3 h-3 rounded-sm bg-[#16A34A]" />
            Follow-up
          </span>
        </div>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 gap-1.5">
        {WEEK_LABELS.map((w) => (
          <div
            key={w}
            className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[var(--swipe-navy)] px-2"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1.5 relative">
        {cells.map((cell) => {
          const items = activityMap.get(cell.ymd) || [];
          const visible = items.slice(0, 3);
          const overflow = items.length - visible.length;
          const cellHandleClick = (e) => onDayClick(cell, e);
          const cellHandleKey = (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onDayClick(cell, e);
            }
          };
          return (
            <div
              key={cell.ymd}
              role="button"
              tabIndex={0}
              onClick={cellHandleClick}
              onKeyDown={cellHandleKey}
              data-testid={`month-cell-${cell.ymd}`}
              className={`group relative text-left rounded-lg border-2 bg-white p-1.5 min-h-[96px] transition-colors hover:border-[#0072FF] hover:bg-[var(--swipe-accent-soft)]/30 focus:outline-none focus:ring-2 focus:ring-[var(--swipe-accent)]/30 cursor-pointer ${
                cell.inMonth ? "border-slate-300" : "border-slate-200 bg-slate-50/60 opacity-60"
              } ${cell.isToday ? "ring-2 ring-[#0072FF] border-[#0072FF] bg-[#EAF3FF]" : ""}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-[12px] font-bold ${
                    cell.isToday
                      ? "text-[var(--swipe-accent)]"
                      : cell.inMonth ? "text-[var(--swipe-navy)]" : "text-slate-400"
                  }`}
                >
                  {cell.day}
                </span>
                {items.length > 0 ? (
                  <span className="text-[9.5px] uppercase font-bold text-slate-400 tabular-nums">
                    {items.length}
                  </span>
                ) : null}
              </div>
              <div className="space-y-1">
                {visible.map((it) => {
                  const eventKey = it.activity.id || `${it.activity.date}-${it.activity.time || "x"}`;
                  return (
                    <MonthEventChip
                      key={`${it.lead.id}-${eventKey}`}
                      item={it}
                      todayStr={today}
                      onComplete={onComplete}
                      onSnooze={onSnooze}
                    />
                  );
                })}
                {overflow > 0 ? (
                  <span className="block text-[10px] text-slate-500 px-1 font-semibold">
                    +{overflow} more
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <DayPopover
        open={!!popover}
        anchor={popover?.anchor}
        ymd={popover?.ymd}
        items={popover?.items || []}
        onClose={() => setPopover(null)}
        onComplete={onComplete}
        onSnooze={onSnooze}
      />
    </div>
  );
}
